/**
 * Shrink a phone photo before upload so it uploads quickly on mobile data
 * and stays well under the server's size limit.
 *
 * Returns the original file when the browser cannot decode it (e.g. HEIC on
 * desktop Chrome) or when compressing would not make it smaller.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<File>}
 */
export default async function compressImage(file, { maxDimension = 1600, quality = 0.8 } = {}) {
    if (!file || !file.type || !file.type.startsWith('image/')) {
        return file;
    }

    let image;
    try {
        image = await loadImage(file);
    } catch (e) {
        return file;
    }

    const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
    const width = Math.round(image.width * scale);
    const height = Math.round(image.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(image, 0, 0, width, height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));

    if (!blob || blob.size >= file.size) {
        return file;
    }

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';

    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Unable to decode image'));
        };
        image.src = url;
    });
}
